import { useEffect, useRef, useState } from "react";
import { ArrowRight, Zap, ChevronDown, ShieldCheck, Cpu, FileJson } from "lucide-react";

const PARTICLES = Array.from({ length: 50 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 1.5 + 0.5,
  speedX: (Math.random() - 0.5) * 0.025,
  speedY: (Math.random() - 0.5) * 0.025,
  opacity: Math.random() * 0.5 + 0.1,
}));

const STATS = [
  { value: "99.9%", label: "API Uptime" },
  { value: "50+", label: "Active Clients" },
  { value: "5", label: "Core Services" },
  { value: "10", label: "Bonus Credits" },
];

const BADGES = [
  { icon: Cpu, label: "AI-Powered" },
  { icon: ShieldCheck, label: "Privacy-First" },
  { icon: FileJson, label: "JSON Output" },
];

// Mock OCR card data
const MOCK_FIELDS = [
  { key: "invoice_number", value: "INV-2025-0042", conf: 1.0 },
  { key: "vendor_name", value: "Tyo Solution", conf: 1.0 },
  { key: "total_amount", value: "Rp 8.214.000", conf: 0.98 },
  { key: "due_date", value: "2025-09-30", conf: 1.0 },
];

export default function Hero() {
  const canvasRef = useRef(null);
  const particlesRef = useRef(PARTICLES.map(p => ({ ...p })));
  const animRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const pts = particlesRef.current;

      // Connections
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const ax = pts[i].x * canvas.width / 100;
          const ay = pts[i].y * canvas.height / 100;
          const bx = pts[j].x * canvas.width / 100;
          const by = pts[j].y * canvas.height / 100;
          const dist = Math.hypot(ax - bx, ay - by);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(bx, by);
            ctx.strokeStyle = `rgba(56,189,248,${0.06 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      pts.forEach(p => {
        const px = p.x * canvas.width / 100;
        const py = p.y * canvas.height / 100;
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(56,189,248,${p.opacity})`;
        ctx.fill();
        p.x += p.speedX; p.y += p.speedY;
        if (p.x < 0) p.x = 100; if (p.x > 100) p.x = 0;
        if (p.y < 0) p.y = 100; if (p.y > 100) p.y = 0;
      });

      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener("resize", resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden hero-bg"
      data-testid="hero-section"
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ opacity: 0.7 }} />
      <div className="absolute inset-0 grid-bg" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-16">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>

          {/* Left */}
          <div>
            {/* Badge */}
            <div className="badge-pill mb-6 w-fit">
              <span className="w-1.5 h-1.5 bg-[#38BDF8] rounded-full pulse-glow inline-block" />
              OCR API · ScoreMyCV · PDF Splitter · Image Optimizer · News Tracking
            </div>

            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight text-white mb-5">
              Automate Smarter.
              <span className="text-[#38BDF8] glow-text"> Scale Faster.</span>
            </h1>

            <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-xl">
              Extract structured data from invoices instantly. Optimize your content for AI search.
              Monitor news in real time — all from one platform, ready via REST API.
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap gap-3 mb-8">
              {BADGES.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-[#94A3B8] border border-white/[0.08] rounded-full px-4 py-1.5 bg-white/[0.03]">
                  <Icon size={13} className="text-[#38BDF8]" />
                  {label}
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-12">
              <a
                href="https://ocr.tyosolution.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#38BDF8] text-[#080f23] font-semibold px-7 py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#0EA5E9] transition-all shadow-[0_0_30px_rgba(56,189,248,0.3)] group"
                data-testid="hero-cta-demo"
              >
                Try OCR Free — 10 Bonus Credits
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <button
                onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-white/[0.06] text-white border border-white/[0.12] hover:bg-white/[0.1] transition-all px-7 py-3.5 rounded-xl font-medium text-sm"
                data-testid="hero-cta-explore"
              >
                View Services
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {STATS.map((stat, i) => (
                <div key={stat.label} data-testid={`hero-stat-${i}`} className="text-center">
                  <div className="text-2xl font-bold text-white mb-0.5">{stat.value}</div>
                  <div className="text-xs text-[#64748B] font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Mock UI card */}
          <div className="hidden lg:flex justify-center items-center" data-testid="hero-mockup">
            <div className="float-animation relative">
              {/* Tooltip badge */}
              <div className="absolute -top-5 -right-4 bg-[#38BDF8] text-[#080f23] text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg z-10">
                Fields Extracted
              </div>

              {/* Main card */}
              <div className="w-[380px] terminal-window shadow-2xl shadow-black/50">
                {/* Header */}
                <div className="terminal-header flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#27C93F]" />
                  <span className="text-xs text-[#64748B] ml-2">invoice_2025.pdf</span>
                  <span className="ml-auto text-xs text-[#38BDF8] font-mono font-medium">Live</span>
                </div>

                {/* Document skeleton */}
                <div className="p-4 space-y-2 border-b border-white/[0.06]">
                  <div className="h-2.5 rounded bg-[#38BDF8]/20 w-3/4" />
                  <div className="h-2 rounded bg-white/10 w-full" />
                  <div className="h-2 rounded bg-[#38BDF8]/15 w-5/6" />
                  <div className="h-2 rounded bg-white/8 w-2/3" />
                  <div className="h-2 rounded bg-white/10 w-full" />
                  <div className="h-2 rounded bg-[#38BDF8]/10 w-4/5" />
                </div>

                {/* Extracted fields */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-4 h-4 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-[#22C55E]" />
                    </div>
                    <span className="text-xs text-[#22C55E] font-semibold">Fields Extracted</span>
                  </div>
                  <div className="space-y-2.5">
                    {MOCK_FIELDS.map(({ key, value, conf }) => (
                      <div key={key} className="flex items-center justify-between text-xs">
                        <span className="text-[#64748B] font-mono">{key}</span>
                        <span className="text-white font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs">
                    <span className="text-[#64748B]">1 credit used</span>
                    <span className="text-[#38BDF8] font-semibold font-mono">499 remaining</span>
                  </div>
                </div>
              </div>

              {/* Confidence badge */}
              <div className="absolute -bottom-3 -left-6 bg-[#0d1635] border border-white/[0.1] rounded-xl px-4 py-2.5 shadow-lg">
                <div className="text-xs text-[#94A3B8] mb-0.5">Confidence score</div>
                <div className="text-sm font-semibold text-white">per field, 0–1.0</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-[#64748B] hover:text-[#38BDF8] transition-colors animate-bounce"
        data-testid="hero-scroll-indicator"
      >
        <ChevronDown size={20} />
      </button>
    </section>
  );
}
